import { getMainValue, setMainValue, hasMainValue, encodeScalarIfDateOrTime } from '../core/EncodeValues.js';
import { Widget } from '../core/Widget.js';
import { mountWidgets } from '../core/WidgetsMount.js';

/**
 * Редактор массива JSON-объектов, строки которого собраны из штатных
 * платформенных контролов.
 */
class StructuredArrayWidget extends Widget {
    constructor() {
        super(...arguments);
        this.rowsContainer = null;
        this.rowTemplate = null;
        this.onClick = (event) => {
            const target = event.target;
            const add = target?.closest("[data-structured-array-add]");
            if (add && this.element.contains(add)) {
                this.appendRow({});
                this._dispatchUserChange();
                return;
            }
            const remove = target?.closest("[data-structured-array-remove]");
            if (!remove || !this.element.contains(remove))
                return;
            remove.closest("[data-structured-array-row]")?.remove();
            if (this.rowElements().length === 0)
                this.appendRow({});
            this._dispatchUserChange();
        };
        this.onNestedChange = (event) => {
            if (event.target === this.element)
                return;
            this._dispatchUserChange();
        };
        this.isStructuredRow = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
    }
    static _cssKey() {
        return "im-structured-array";
    }
    _create() {
        this.element._customChange = true;
        this.rowsContainer = this.element.querySelector("[data-structured-array-rows]");
        this.rowTemplate = this.element.querySelector("template[data-structured-array-template]");
        this.element.addEventListener("click", this.onClick);
        this.element.addEventListener("change", this.onNestedChange);
        this.element.addEventListener("ddchange", this.onNestedChange);
    }
    getValue() {
        return JSON.stringify(this.readRows());
    }
    setValue(value) {
        if (!this.rowsContainer || !this.rowTemplate)
            return false;
        const rows = this.parseRows(value);
        this.rowsContainer.replaceChildren();
        if (rows.length === 0) {
            this.appendRow({});
            return true;
        }
        rows.forEach(row => this.appendRow(row));
        return true;
    }
    hasValue() {
        return this.readRows().length > 0;
    }
    _onDestroy() {
        this.element.removeEventListener("click", this.onClick);
        this.element.removeEventListener("change", this.onNestedChange);
        this.element.removeEventListener("ddchange", this.onNestedChange);
        this.rowsContainer = null;
        this.rowTemplate = null;
    }
    readRows() {
        const result = [];
        for (const rowElement of this.rowElements()) {
            const row = {};
            let hasValue = false;
            this.fieldElements(rowElement).forEach(field => {
                const key = field.dataset.structuredField;
                if (!key)
                    return;
                const control = this.findControl(field);
                const value = control ? getMainValue(control) : undefined;
                const normalized = this.normalizeNestedValue(control, value);
                row[key] = normalized ?? null;
                if (this.isPresent(normalized))
                    hasValue = true;
            });
            if (hasValue)
                result.push(row);
        }
        return result;
    }
    appendRow(values) {
        if (!this.rowsContainer || !this.rowTemplate)
            return null;
        const row = this.rowTemplate.content.firstElementChild
            ?.cloneNode(true);
        if (!row)
            return null;
        this.rowsContainer.appendChild(row);
        mountWidgets(row);
        this.fieldElements(row).forEach(field => {
            const key = field.dataset.structuredField;
            if (!key || !Object.prototype.hasOwnProperty.call(values, key))
                return;
            const control = this.findControl(field);
            if (control)
                setMainValue(control, values[key]);
        });
        return row;
    }
    rowElements() {
        if (!this.rowsContainer)
            return [];
        return Array.from(this.rowsContainer.children)
            .filter((element) => element instanceof HTMLElement
            && element.hasAttribute("data-structured-array-row"));
    }
    fieldElements(row) {
        return Array.from(row.children)
            .filter((element) => element instanceof HTMLElement
            && element.hasAttribute("data-structured-field"));
    }
    findControl(field) {
        const candidates = field.querySelectorAll("[data-widget], .dropdown, .im-combo, input, select, textarea");
        for (const candidate of Array.from(candidates)) {
            if (hasMainValue(candidate) !== undefined)
                return candidate;
        }
        return candidates.item(0);
    }
    parseRows(value) {
        if (value === null || value === undefined || value === "dbnull")
            return [];
        if (Array.isArray(value))
            return value.filter(this.isStructuredRow);
        if (typeof value !== "string" || value.trim() === "")
            return [];
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed)
                ? parsed.filter(this.isStructuredRow)
                : [];
        }
        catch {
            return [];
        }
    }
    /**
     * Вложенный StructuredArray отдаёт самостоятельному form-encoder JSON-строку.
     * Внутри родительской строки это значение должно остаться массивом, иначе JSON
     * превращается в `{ "members": "[]" }` и нарушает серверную схему.
     */
    normalizeNestedValue(control, value) {
        if (control?.dataset.widget === StructuredArrayWidget._alias)
            return this.parseRows(value);
        return encodeScalarIfDateOrTime(value);
    }
    isPresent(value) {
        if (Array.isArray(value))
            return value.length > 0;
        return value !== null
            && value !== undefined
            && !(typeof value === "string" && value.trim() === "");
    }
}
StructuredArrayWidget._alias = "StructuredArray";

export { StructuredArrayWidget };
